import { Link } from "react-router-dom";
import { Mic, Play, Calendar, ArrowRight, Headphones, Youtube, Instagram, Facebook, Twitter, Music2, Newspaper, Podcast as PodcastIcon, Linkedin } from "lucide-react";
import { SOCIAL_LINKS } from "../data/social";
import { EPISODES, ALL_EPISODES_COUNT as TOTAL_EPISODE_COUNT, YOUTUBE_CHANNEL_URL, SPOTIFY_SHOW_ID } from "../data/episodes";

const ICONS = { Linkedin, Youtube, Instagram, Facebook, Twitter, Music2, Newspaper, Podcast: PodcastIcon };

const LISTEN_PLATFORMS = ["spotify", "youtube", "substack"];

const PRACTICAL_TIPS = {
  playlist_id: "PLM07A11AbCNiOaQG6MCRn1ay4eJdA970_",
  first_video_id: "SgE8KWSHMfE",
  playlist_url: "https://www.youtube.com/playlist?list=PLM07A11AbCNiOaQG6MCRn1ay4eJdA970_",
  cover: "/practical-tips-cover.png",
  bullets: [
    "Short, tactical lessons drawn from real projects",
    "Stakeholder engagement, SIA method, risk registers, monitoring",
    "Made for practitioners, students and delivery teams",
  ],
};

// Episodes come from /data/episodes.js.


export default function Podcast() {
  return (
    <div data-testid="podcast-page">
      {/* Hero */}
      <section className="bg-brand-blue text-white py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-14 items-center">
          <div>
            <div className="eyebrow text-brand-yellow mb-6 inline-flex items-center gap-2">
              <Headphones className="w-4 h-4" /> The podcast
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Voices of Change</h1>
            <p className="text-lg lg:text-xl text-brand-yellow font-semibold mt-4 max-w-3xl">Stories that matter.</p>
            <p className="text-white/80 text-base lg:text-lg mt-6 max-w-2xl leading-relaxed">
              Conversations with practitioners, executives, communities and regulators about the real work behind social impact - what changes decisions, what changes lives, and what quietly gets left out.
            </p>
            <p className="text-white/70 text-sm mt-6 max-w-2xl">Hosted by Andrea Kanaris. {TOTAL_EPISODE_COUNT}+ episodes out now on YouTube and Spotify, plus the companion series <em>Practical Tips for Social Impact & Project Management</em>.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact" className="btn-primary" data-testid="podcast-cta-discuss">
                Suggest a topic <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#episodes" className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-brand-blue transition-colors">
                <Play className="w-4 h-4" /> Episode line-up
              </a>
            </div>
          </div>
          <div className="relative w-full max-w-[360px] mx-auto lg:mx-0">
            <div className="absolute -inset-4 bg-brand-orange/20 blur-2xl -z-0" aria-hidden="true"></div>
            <img
              src="/voices-of-change.png"
              alt="Voices of Change - Stories That Matter podcast cover art"
              data-testid="podcast-cover"
              className="relative w-full h-auto shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Featured episode player */}
      <section data-testid="podcast-featured-episode" className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <div className="w-full aspect-video bg-black shadow-xl">
            <iframe
              data-testid="episode-player"
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${EPISODES[0].youtube_id}?rel=0`}
              title={EPISODES[0].title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-orange mb-3">{EPISODES[0].number} · Now available</div>
            <h2 className="font-serif text-3xl text-brand-blue leading-tight mb-4">{EPISODES[0].title}</h2>
            <p className="text-slate-700 leading-relaxed mb-6">{EPISODES[0].excerpt}</p>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">With {EPISODES[0].guest}</div>
            <a
              href={EPISODES[0].youtube_url}
              target="_blank"
              rel="noreferrer"
              data-testid="episode-watch-youtube"
              className="inline-flex items-center gap-2 border border-brand-blue text-brand-blue px-5 py-3 text-sm font-semibold hover:bg-brand-blue hover:text-white transition-colors"
            >
              <Youtube className="w-4 h-4" /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* Practical Tips companion series */}
      <section data-testid="practical-tips-section" className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
            <div>
              <div className="eyebrow mb-4">Companion series</div>
              <h2 className="font-serif text-3xl lg:text-4xl leading-tight mb-4">Practical Tips for Social Impact & Project Management</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                Short, sharp video tips for practitioners, students and delivery teams. The complement to Voices of Change - if the podcast is the "why", Practical Tips is the "how".
              </p>
              <ul className="space-y-3 mb-8">
                {PRACTICAL_TIPS.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="w-1.5 h-1.5 mt-2 bg-brand-orange flex-shrink-0" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mb-6 shadow-md">
                <img
                  src={PRACTICAL_TIPS.cover}
                  alt="Practical Tips for Social Impact & Project Management - video series cover"
                  data-testid="practical-tips-cover"
                  className="w-full h-auto"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <a href={PRACTICAL_TIPS.playlist_url} target="_blank" rel="noreferrer"
                  data-testid="practical-tips-cta"
                  className="btn-primary">
                  <Youtube className="w-4 h-4" /> Open full playlist
                </a>
                <Link to="/practitioners" className="btn-secondary" data-testid="practical-tips-practitioners">
                  For practitioners
                </Link>
              </div>
            </div>
            <div className="w-full">
              <div className="aspect-video bg-black shadow-xl">
                <iframe
                  data-testid="practical-tips-player"
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${PRACTICAL_TIPS.first_video_id}?list=${PRACTICAL_TIPS.playlist_id}&rel=0`}
                  title="Practical Tips for Social Impact & Project Management - playlist"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-4 text-center">
                Auto-plays the full series in order
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Listen on */}
      <section data-testid="podcast-listen-on" className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6 text-center">Listen and follow</div>

          {/* Embedded Spotify show player - always shows the latest episode */}
          <div className="max-w-[720px] mx-auto mb-8" data-testid="spotify-embed-wrap">
            <iframe
              data-testid="spotify-player"
              src={`https://open.spotify.com/embed/show/${SPOTIFY_SHOW_ID}?utm_source=generator&theme=0`}
              width="100%"
              height="232"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Voices of Change - Spotify player"
              className="shadow-md"
            />
          </div>

          {/* Secondary platforms */}
          <div className="flex flex-wrap justify-center gap-3">
            {SOCIAL_LINKS.filter((s) => LISTEN_PLATFORMS.includes(s.key) && s.key !== "spotify").map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <a key={s.key} href={s.url} target="_blank" rel="noreferrer" data-testid={`podcast-listen-${s.key}`}
                  className="inline-flex items-center gap-3 border border-slate-300 px-5 py-3 text-sm font-semibold hover:border-brand-orange hover:text-brand-orange transition-colors">
                  {Icon && <Icon className="w-4 h-4" />}
                  <span>{s.label}</span>
                  <span className="text-xs text-slate-500 uppercase tracking-widest">{s.handle}</span>
                </a>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <div className="eyebrow text-slate-500 mb-4">Also on</div>
            <div className="flex flex-wrap justify-center gap-2">
              {SOCIAL_LINKS.filter((s) => !LISTEN_PLATFORMS.includes(s.key)).map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <a key={s.key} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label} title={`${s.label} · ${s.handle}`}
                    data-testid={`podcast-social-${s.key}`}
                    className="w-10 h-10 flex items-center justify-center border border-slate-300 text-slate-600 hover:border-brand-orange hover:text-brand-orange transition-colors">
                    {Icon && <Icon className="w-4 h-4" />}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Real voices", body: "Not a highlight reel. Practitioners talking about the moment a project turned, the community that pushed back, the mitigation that quietly worked." },
            { title: "Grounded in evidence", body: "Every episode traces the frameworks, cases and data behind the story. No abstract 'ESG' talk - the specifics that matter to how projects actually get built." },
            { title: "Lasting change", body: "Community outcomes that outlast the project team. Governance, benefit-sharing and monitoring that survive year three, year five, year ten." },
          ].map((c) => (
            <div key={c.title}>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-orange mb-3">{c.title}</div>
              <p className="text-slate-700 leading-relaxed text-sm">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Episodes */}
      <section id="episodes" className="py-20 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-3">Featured · {TOTAL_EPISODE_COUNT} episodes in total</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-2 max-w-2xl">The conversations to start with.</h2>
          <p className="text-slate-600 max-w-2xl mb-10">Four openers that map the territory - social value, rural resilience, professional ethics and the future of ENS practice. The rest of the back catalogue plus the <em>Practical Tips for Social Impact & Project Management</em> series lives on YouTube.</p>
          <div className="divide-y divide-slate-200 border-t border-slate-200">
            {EPISODES.map((ep) => {
              const isPublished = ep.status === "published";
              return (
                <article key={ep.slug} data-testid={`episode-${ep.slug}`} className="py-8 grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-6 items-start">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-orange">{ep.number}</div>
                    <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" /> {ep.releaseDate}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-brand-blue mb-2 leading-snug">{ep.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm mb-3">{ep.excerpt}</p>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">
                      With {ep.guest} · {ep.duration}
                    </div>
                  </div>
                  <div>
                    {isPublished ? (
                      <Link to={`/podcast/${ep.slug}`} data-testid={`episode-cta-${ep.slug}`}
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold bg-brand-orange text-white px-3 py-2 hover:-translate-y-0.5 transition-transform">
                        <Play className="w-3 h-3" /> Show notes
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold bg-brand-yellow text-brand-blue px-3 py-2">
                        <Mic className="w-3 h-3" /> Upcoming
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
          <div className="mt-10 pt-8 border-t border-slate-200 text-center">
            <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noreferrer" data-testid="podcast-full-library" className="inline-flex items-center gap-2 text-brand-orange font-semibold text-sm hover:underline">
              See all {TOTAL_EPISODE_COUNT} episodes + Practical Tips series on YouTube <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <div className="eyebrow text-brand-yellow mb-4">Be first to hear</div>
          <h2 className="font-serif text-3xl lg:text-4xl leading-tight mb-4">Get every new episode in your inbox.</h2>
          <p className="text-white/80 max-w-xl mx-auto leading-relaxed mb-8">
            Join the Insights list to hear when a new Voices of Change episode drops. No spam, no filler - just new work as it goes out.
          </p>
          <Link to="/insights" className="btn-primary" data-testid="podcast-subscribe">
            Subscribe via Insights <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
